import { logger } from '@heidi/runtime/logger';
import { useCallback, useEffect, useRef, useState } from 'react';

import { type JoinedWorkspaceRelation } from '../../contracts';
import { setWorkspaceId } from '../atoms/workspaceAtoms';
import {
  getWorkspaceDataConfig,
  isTransportError,
  type MultiWorkspaceSelectionGate,
} from '../config';
import { useQueryWorkspaces } from './useQueryWorkspaces';
import { useWorkspaceId } from './useWorkspaceId';

/**
 * Watchdog for the cold-boot `GET /workspaces` call — `scribeHTTP` sets no
 * axios timeout, so a hanging request would otherwise pin the loader forever.
 * Sized just past the worst-case automatic retry window (`retry: 3`,
 * exponential backoff); firing during a final in-flight retry is safe.
 */
const WORKSPACE_HYDRATION_TIMEOUT_MS = 10_000;

export type WorkspaceHydrationDegradedReason =
  | 'workspaces_query_error'
  | 'hydration_timeout'
  | 'no_workspaces'
  | 'user_not_provisioned'
  | 'session_expired';

export type WorkspaceHydrationStatus =
  | { state: 'resolved' }
  | { state: 'pending' }
  | {
      /**
       * the user belongs to 2+ workspaces, none is persisted yet
       * (a fresh login) and the selector flag is ON — the gate holds until the
       * user picks. `select` persists the choice and releases the gate.
       */
      state: 'awaiting_selection';
      workspaces: JoinedWorkspaceRelation[];
      select: (workspaceId: string) => void;
    }
  | {
      state: 'degraded';
      reason: WorkspaceHydrationDegradedReason;
      /** Re-runs the workspaces query and re-arms the timeout watchdog. */
      retry: () => void;
    };

/** The two reasons consumers render through instead of blocking on. */
type FailOpenVerdict = 'user_not_provisioned' | 'session_expired';

/**
 * Resolves the degraded reason (if any) from the hydration signals, in priority
 * order. Returns `null` when there is nothing to wait for or no failure yet.
 */
function getDegradedReason({
  needsWorkspace,
  failOpenVerdict,
  hasSettledError,
  hasNoWorkspaces,
  timedOut,
}: {
  needsWorkspace: boolean;
  /** Fail-open classification of the most recent settled error — live, or
   * latched across a refetch (see the latch in the hook). */
  failOpenVerdict: FailOpenVerdict | undefined;
  hasSettledError: boolean;
  hasNoWorkspaces: boolean;
  timedOut: boolean;
}): WorkspaceHydrationDegradedReason | null {
  if (!needsWorkspace) return null;
  if (failOpenVerdict) return failOpenVerdict;
  if (hasSettledError) return 'workspaces_query_error';
  if (hasNoWorkspaces) return 'no_workspaces';
  if (timedOut) return 'hydration_timeout';
  return null;
}

/** Why the gate is still holding once nothing is degraded — `null` means nothing to wait for. */
type HoldReason = 'workspaces_query' | 'selection_flag' | 'user_selection';

/**
 * Resolves the hold reason (if any) in priority order, the sibling of
 * `getDegradedReason` for the non-failure holds.
 */
function getHoldReason({
  needsWorkspace,
  degradedReason,
  hasWorkspaces,
  hasMultipleWorkspaces,
  selectionGate,
  isJoinTeamRoute,
}: {
  needsWorkspace: boolean;
  degradedReason: WorkspaceHydrationDegradedReason | null;
  /** The workspaces query has settled with data. */
  hasWorkspaces: boolean;
  hasMultipleWorkspaces: boolean;
  selectionGate: MultiWorkspaceSelectionGate;
  isJoinTeamRoute: boolean;
}): HoldReason | null {
  if (!needsWorkspace || degradedReason !== null) return null;
  if (!hasWorkspaces) return 'workspaces_query';
  // One workspace: the boot writer auto-picks it, nothing to hold for.
  if (!hasMultipleWorkspaces) return null;
  // The selector's flag is read pre-identity and defaults OFF until the
  // provider settles, so an unsettled flag HOLDS (the hydrate writer defers on
  // the same gate).
  if (selectionGate.isAwaitingFlag) return 'selection_flag';
  // An invitee on /join-team is auto-picked and switched by the accept flow,
  // never held on the selector (mirrors the boot writer in `AuthWrapper`).
  if (selectionGate.isSelectionEnabled && !isJoinTeamRoute) return 'user_selection';
  return null;
}

/**
 * Timing truth for surfaces that block on a resolved workspace
 * (`WorkspaceHydrationGate`). Reads the shared `useQueryWorkspaces` cache
 * (`useHydrateWorkspaceId` stays the atom's only writer); owns the timeout
 * watchdog and the one-shot audit logs (MUL-1991).
 *
 * Failures BLOCK (fail closed) except two verdicts consumers render through,
 * because the surface that resolves them lives BELOW the gate:
 * `user_not_provisioned` (settled 400 — the signup form) and
 * `session_expired` (settled non-transient 401 — the login page, MUL-2568).
 *
 * Those verdicts are classified at settle time and LATCHED in hook state:
 * released children refetch the shared query on mount, and a refetch with no
 * data yet resets the query's `error`, so the live query cannot carry the
 * verdict. The latch moves only on a new settled verdict, clears on success
 * or episode end (`needsWorkspace` falls), and ignores transport failures
 * (5xx, status-less, transient 401) — re-blocking on those would unmount a
 * form the user is filling in.
 *
 * Deliberately NOT opened early on logout-in-flight or auth-cache signals:
 * they run ahead of the Redux token, and opening early would release
 * header-less requests — the traffic this gate exists to block.
 *
 * Residual gap: on the legacy interceptor path a spontaneous-expiry 401 is
 * swallowed upstream into a status-less error and fails closed. Revisit once
 * the legacy path is retired.
 *
 * Extracted to @heidi/workspace/data — history:
 * `git log --follow src/features/workspace/hooks/useWorkspaceHydrationStatus.ts`.
 */
export function useWorkspaceHydrationStatus(): WorkspaceHydrationStatus {
  const {
    useAuthToken,
    useIsTransientAuthFailure,
    useMultiWorkspaceSelectionGate,
    useIsJoinTeamRoute,
  } = getWorkspaceDataConfig();
  const token = useAuthToken();
  const isTransientAuthFailure = useIsTransientAuthFailure();
  const workspaceId = useWorkspaceId();
  const { data, error, isError, isFetching, isSuccess, refetch } = useQueryWorkspaces();
  const selectionGate = useMultiWorkspaceSelectionGate();
  // Mirrors the boot writer (`AuthWrapper`): an invitee on /join-team is
  // auto-picked and switched by the accept flow, never held on the selector.
  const isJoinTeamRoute = useIsJoinTeamRoute();

  const [timedOut, setTimedOut] = useState(false);
  // Most recent settled fail-open verdict, held across the refetches the
  // released subtree triggers — see the latch paragraph in the docstring.
  const [latchedVerdict, setLatchedVerdict] = useState<FailOpenVerdict | undefined>(
    undefined
  );
  const degradedLoggedRef = useRef(false);
  const userNotProvisionedLoggedRef = useRef(false);
  const sessionExpiredLoggedRef = useRef(false);
  const selectionRequiredLoggedRef = useRef(false);

  const needsWorkspace = Boolean(token) && workspaceId === null;
  // Settled empty list = invariant violation (every user must have ≥1 workspace),
  // surfaced as a degraded error. `data &&` guards the still-loading (`undefined`)
  // case so an in-flight query isn't misread as empty.
  const hasNoWorkspaces = data && data.workspaces.length === 0;
  const hasSettledError = isError && !isFetching;

  // This is a vague assumption that 400 status code means "user not found"
  // It currently works like that, but if you're coming from the future it might well not be!
  //
  // Narrowed from `unknown`, as the retry policy's own callback is: `error` is
  // typed `Error`, which already satisfies the structural transport view, so a
  // predicate applied to it would narrow to nothing.
  const settledError: unknown = error;
  const settledErrorStatus =
    hasSettledError && isTransportError(settledError)
      ? settledError.response?.status
      : undefined;

  // Classified at settle time so the verdict is frozen: a later change of the
  // transient flag can never re-read an old 401 as an expiry.
  const settledVerdict: FailOpenVerdict | undefined =
    settledErrorStatus === 400
      ? 'user_not_provisioned'
      : settledErrorStatus === 401 && !isTransientAuthFailure
        ? 'session_expired'
        : undefined;

  // Latch transitions run DURING render (React's adjust-state-during-render
  // pattern), never in an effect: the verdict must be committed before the
  // released children mount, because their mount refetches the shared query
  // and resets its error — an effect would leave a window with neither the
  // live verdict nor the latch, and the gate would re-block.
  let nextLatchedVerdict = latchedVerdict;
  if (!needsWorkspace || isSuccess) {
    nextLatchedVerdict = undefined;
  } else if (settledVerdict !== undefined) {
    nextLatchedVerdict = settledVerdict;
  }
  if (nextLatchedVerdict !== latchedVerdict) {
    setLatchedVerdict(nextLatchedVerdict);
  }

  const degradedReason = getDegradedReason({
    needsWorkspace,
    failOpenVerdict: nextLatchedVerdict,
    hasSettledError,
    hasNoWorkspaces: Boolean(hasNoWorkspaces),
    timedOut,
  });

  const holdReason = getHoldReason({
    needsWorkspace,
    degradedReason,
    hasWorkspaces: data !== undefined,
    hasMultipleWorkspaces: (data?.workspaces.length ?? 0) > 1,
    selectionGate,
    isJoinTeamRoute,
  });

  // The watchdog bounds the two holds the user cannot end themselves, so a
  // provider that never settles degrades rather than pinning the loader. The
  // selector never arms it — a user may take their time.
  const isAwaitingWorkspaces =
    holdReason === 'workspaces_query' || holdReason === 'selection_flag';
  const isAwaitingSelection = holdReason === 'user_selection';

  useEffect(() => {
    if (!isAwaitingWorkspaces) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setTimedOut(true);
    }, WORKSPACE_HYDRATION_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isAwaitingWorkspaces]);

  useEffect(() => {
    if (needsWorkspace) {
      return;
    }

    setTimedOut(false);

    degradedLoggedRef.current = false;
    userNotProvisionedLoggedRef.current = false;
    sessionExpiredLoggedRef.current = false;
    selectionRequiredLoggedRef.current = false;
  }, [needsWorkspace]);

  useEffect(() => {
    if (!isAwaitingSelection || selectionRequiredLoggedRef.current) {
      return;
    }
    selectionRequiredLoggedRef.current = true;
    // One-shot per episode, so the selector's reach is observable. Count only —
    // no workspace names or ids.
    logger.info(
      {
        event: 'workspace_selection_required',
        workspaceCount: data?.workspaces.length ?? 0,
      },
      'Workspace hydration: multi-workspace user, holding for a selection'
    );
  }, [isAwaitingSelection, data]);

  useEffect(() => {
    if (degradedReason === null) {
      // no error
      return;
    }

    if (degradedReason === 'session_expired') {
      // Fail-open; cleanup owned upstream. Info-level (not a blocking
      // degradation), one-shot per episode.
      if (sessionExpiredLoggedRef.current) {
        return;
      }
      sessionExpiredLoggedRef.current = true;
      logger.info(
        { event: 'workspace_hydration_session_expired' },
        'Workspace hydration: 401 on workspaces query, failing open'
      );
      return;
    }

    if (degradedReason === 'user_not_provisioned') {
      // Fail-open (signup window). Info-level so the branch rate stays
      // observable — the reason keys off a bare 400, and this log is the only
      // signal if that heuristic drifts. One-shot per episode.
      if (userNotProvisionedLoggedRef.current) {
        return;
      }
      userNotProvisionedLoggedRef.current = true;
      logger.info(
        { event: 'workspace_hydration_user_not_provisioned' },
        'Workspace hydration: user not provisioned yet, failing open'
      );
      return;
    }

    if (degradedLoggedRef.current) {
      // already logged the error - might be some react re-rendering issue, don't spam!
      return;
    }

    degradedLoggedRef.current = true;

    logger.warn(
      { event: 'workspace_hydration_degraded', reason: degradedReason },
      'Workspace hydration degraded for authenticated session'
    );
  }, [degradedReason]);

  const retry = useCallback(() => {
    setTimedOut(false);

    void refetch();
  }, [refetch]);

  // First pick, not a switch: nothing below the gate has loaded under another
  // workspace, so setting the id is enough — the atom turning non-null releases
  // the gate. (`switchWorkspace` is for a switch the caller reloads after; its
  // payload here would linger into the next boot's switch loader.)
  // Only a workspace from the user's own list is persisted — the surface
  // renders nothing else, but the id crosses as a string, so the membership
  // check lives here rather than being assumed of the caller.
  const select = useCallback(
    (selectedWorkspaceId: string) => {
      if (!data?.workspaces.some((w) => w.workspace_id === selectedWorkspaceId)) {
        return;
      }
      setWorkspaceId(selectedWorkspaceId);
    },
    [data]
  );

  if (!needsWorkspace) {
    return { state: 'resolved' };
  }

  if (degradedReason !== null) {
    return { state: 'degraded', reason: degradedReason, retry };
  }

  if (isAwaitingSelection && data) {
    return { state: 'awaiting_selection', workspaces: data.workspaces, select };
  }

  return { state: 'pending' };
}
